import { ShieldCheck, ScrollText, LineChart, Sparkles } from "lucide-react";

const CARDS = [
  {
    id: "commitment",
    Icon: ShieldCheck,
    title: "Our Commitment",
    eyebrow: "What we guarantee",
    body: [
      "We are committed to delivering the services included in your selected plan with professionalism, consistency, and excellence.",
      "Our responsibility is to execute the marketing activities outlined in your plan, provide ongoing support, and help create more opportunities for you to grow your subscriber base.",
      "We stand behind the quality and consistency of the services we provide.",
    ],
  },
  {
    id: "income",
    Icon: ScrollText,
    title: "Income Disclaimer",
    eyebrow: "What we do not guarantee",
    body: [
      "While we guarantee the delivery of the services included in your selected plan, we do not guarantee any specific income or earnings, a specific number of subscribers, closed sales, commission amounts, business growth, or financial success.",
      "Your results depend on many factors — including your consistency, follow-up, communication, sales ability, responsiveness, market conditions, subscriber retention, and overall implementation.",
      "Every Brand Affiliate's journey is different.",
    ],
  },
  {
    id: "illustration",
    Icon: LineChart,
    title: "Recurring Income Illustration",
    eyebrow: "Illustrative purposes only",
    body: [
      "The recurring income examples shown throughout this page — including the 100 Subscriber Plan and $10,000/month recurring income illustrations — are provided for educational and illustrative purposes only.",
      "These examples are intended to demonstrate how a recurring income business can grow over time and assume an average Brand Affiliate recurring commission of approximately $100 per active WinBiggest subscriber per month.",
      "Actual results will vary.",
    ],
  },
];

export default function OurCommitment() {
  return (
    <section
      data-testid="wc-commitment"
      className="bg-[#f8f6ff] py-24 sm:py-32"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white text-[#673dfc] border border-[#e5dfff] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-5">
            <Sparkles size={13} /> Transparency & trust
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-5xl lg:text-[52px] tracking-tight text-[#30265b] leading-[1.05]">
            Our commitment to{" "}
            <span className="text-[#673dfc]">your success.</span>
          </h2>
          <div className="mt-6 space-y-4 text-lg text-gray-700 leading-relaxed">
            <p>
              At WinCampaign, we&apos;re committed to helping Brand Affiliates
              build a sustainable recurring income business through consistent
              marketing, lead generation, and proven business systems.
            </p>
            <p>
              <span className="font-display font-semibold text-[#30265b]">
                Our goal is simple:
              </span>{" "}
              to become your dedicated marketing partner so you can spend more
              time building relationships, gaining subscribers, and growing
              your business.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-14 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {CARDS.map((c) => {
            const Icon = c.Icon;
            return (
              <div
                key={c.id}
                data-testid={`wc-commitment-${c.id}`}
                className="rounded-3xl bg-white border border-gray-100 p-7 sm:p-8 flex flex-col hover:border-[#c5b4ff] hover:shadow-lg hover:shadow-[#673dfc]/5 transition-all duration-500"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#f3efff] text-[#673dfc] grid place-items-center">
                  <Icon size={22} strokeWidth={1.75} />
                </div>
                <div className="mt-6 text-[11px] font-nav font-semibold uppercase tracking-[0.14em] text-[#673dfc]">
                  {c.eyebrow}
                </div>
                <h3 className="mt-1 font-display font-bold text-xl sm:text-2xl text-[#30265b] leading-tight">
                  {c.title}
                </h3>
                <div className="mt-4 space-y-3">
                  {c.body.map((p, i) => (
                    <p
                      key={i}
                      className="text-sm sm:text-[15px] text-gray-600 leading-relaxed"
                    >
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Closing statement */}
        <div className="mt-14 rounded-3xl bg-[#30265b] text-white p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#673dfc]/40 blur-3xl" />
          <div className="relative max-w-3xl">
            <div className="text-xs font-nav font-semibold uppercase tracking-[0.14em] text-[#d3fc3a]">
              Our promise
            </div>
            <h3 className="mt-2 font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl leading-[1.1]">
              We&apos;ll consistently execute the marketing and business
              growth services included in your selected plan.
            </h3>
            <div className="mt-5 space-y-3 text-white/80 leading-relaxed">
              <p>
                What you do with the opportunities we help create is ultimately
                what determines your long-term success.
              </p>
              <p className="font-display font-semibold text-white">
                We&apos;re committed to helping you build your recurring
                income business — one subscriber at a time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
